/**
 * Owner: Masa Bayu
 * File: demo-library-base.ts
 * Date:Time: 2026-10-18 19:20
 * Description: T4 library store §377 (bahagian 1/3) — keadaan module tunggal
 *   (demoLibraryState) + projeksi. §458: seed rekaan daripada katalog dibuang;
 *   hak bacaan hanya masuk melalui grant (pembayaran / `applyExternalHubGrants`).
 */

import {
  createLibrarySyncContract,
  createQxreadTelemetryEvent,
  projectLibraryFromEntitlements,
  type QxreadEntitlement,
  type QxreadLibraryFilterKey,
  type QxreadLibraryItem,
  type QxreadLibraryProjection,
  type QxreadLibraryReaderLaunchIntent,
  type QxreadLibraryResumeIntent,
  type QxreadLibrarySortKey,
  type QxreadLibrarySurfaceView,
  type QxreadLibrarySyncContract,
  type QxreadTelemetryEvent,
  type QxreadWishlistItem,
} from '@qx/qxread-core';
import { listCatalog } from '../publication/catalog-port';

export const DEMO_ACCOUNT_ID = 'qxrdacc_demo_reader';

export const demoLibraryState: {
  entitlements: QxreadEntitlement[];
  itemsByEntitlementId: Map<string, QxreadLibraryItem>;
  wishlist: QxreadWishlistItem[];
  sync: QxreadLibrarySyncContract;
  lastLaunchIntent: QxreadLibraryReaderLaunchIntent | null;
  lastResumeIntent: QxreadLibraryResumeIntent | null;
  lastTelemetry: QxreadTelemetryEvent | null;
  lastSurface: QxreadLibrarySurfaceView | null;
  queryFilter: QxreadLibraryFilterKey;
  querySort: QxreadLibrarySortKey;
  queryText: string;
} = {
  entitlements: [],
  itemsByEntitlementId: new Map<string, QxreadLibraryItem>(),
  wishlist: [],
  sync: createLibrarySyncContract({ accountId: DEMO_ACCOUNT_ID }),
  lastLaunchIntent: null,
  lastResumeIntent: null,
  lastTelemetry: null,
  lastSurface: null,
  queryFilter: 'all',
  querySort: 'recent',
  queryText: '',
};

const libraryListeners = new Set<() => void>();
let libraryRevision = 0;

/** Mutations made outside a surface's own React tree (e.g. product chrome) announce here. */
export function bumpDemoLibraryRevision(): void {
  libraryRevision += 1;
  for (const listener of libraryListeners) {
    listener();
  }
}

export function getDemoLibraryRevision(): number {
  return libraryRevision;
}

export function subscribeDemoLibraryRevision(listener: () => void): () => void {
  libraryListeners.add(listener);
  return () => {
    libraryListeners.delete(listener);
  };
}

/** Active catalog SoT — production when host port bound · demo otherwise. */
function catalogMap() {
  const map = new Map<
    string,
    {
      publicationId: string;
      title: string;
      qxpPublicationRef: string | null;
      pinnedVersionLabel: string | null;
      listedVersionLabel: string | null;
    }
  >();
  for (const entry of listCatalog()) {
    const versionLabel = entry.version?.versionLabel ?? entry.listedVersion;
    map.set(entry.publicationId, {
      publicationId: entry.publicationId,
      title: entry.title,
      qxpPublicationRef: entry.qxpPublicationRef,
      pinnedVersionLabel: versionLabel,
      listedVersionLabel: versionLabel,
    });
  }
  return map;
}

export function authorsMap(): ReadonlyMap<string, readonly string[]> {
  const map = new Map<string, readonly string[]>();
  for (const entry of listCatalog()) {
    const authors = entry.metadata.authors;
    if (Array.isArray(authors)) {
      map.set(entry.publicationId, authors);
    }
  }
  return map;
}

export function record(
  name:
    | 'qxread.library.projected'
    | 'qxread.library.progress_updated'
    | 'qxread.library.offline_decision'
    | 'qxread.library.sync_state'
    | 'qxread.library.launch_intent'
    | 'qxread.library.resume_intent'
    | 'qxread.library.query'
    | 'qxread.library.version_awareness',
  attributes: Readonly<Record<string, string | number | boolean | null>>,
): void {
  demoLibraryState.lastTelemetry = createQxreadTelemetryEvent({
    name,
    correlationId: DEMO_ACCOUNT_ID,
    attributes,
  });
}

export function getDemoLibraryAccountId(): string {
  return DEMO_ACCOUNT_ID;
}

/**
 * §458 — hak bacaan HANYA lahir daripada grant (pembayaran disahkan /
 *   `applyExternalHubGrants` dari hub). Tiada lagi seed rekaan daripada katalog:
 *   `listCatalog()[0..1]` dahulu di-grant `owned` kepada setiap akaun, termasuk
 *   yang `/v1/entitlements` → `grants: []`, dan ia melanggar invarian yang sudah
 *   ditulis di hub-sync.ts ("catalog/UI tidak pernah menggrants akses").
 */
export function getDemoLibraryProjection(): QxreadLibraryProjection {
  const projection = projectLibraryFromEntitlements({
    accountId: DEMO_ACCOUNT_ID,
    entitlements: demoLibraryState.entitlements,
    catalogByPublicationId: catalogMap(),
    existingItemsByEntitlementId: demoLibraryState.itemsByEntitlementId,
    wishlist: demoLibraryState.wishlist,
  });
  for (const item of [
    ...projection.purchased,
    ...projection.continueReading,
    ...projection.completed,
    ...projection.blocked,
    ...projection.archived,
  ]) {
    demoLibraryState.itemsByEntitlementId.set(item.entitlementId, item);
  }
  record('qxread.library.projected', {
    purchased: projection.purchased.length,
    continueReading: projection.continueReading.length,
    completed: projection.completed.length,
    blocked: projection.blocked.length,
    archived: projection.archived.length,
    wishlist: projection.wishlist.length,
  });
  return projection;
}
